import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CreditCard, Building2, Wallet, ChevronDown, Check, AlertCircle, Lock, Shield, CheckCircle2, Loader2, User, Globe, History, Key, Users, Monitor, Mail, Phone, Laptop, Smartphone, Minus, Plus, RotateCw } from "lucide-react";
import FozzilPay from "../utils/FozzilPaySDK";
import { FaUniversity } from "react-icons/fa";
import { supabase } from "../supabaseClient";
import PaymentsComingSoon from "./PaymentsComingSoon";

// Assets 
const logoDarkTheme = "/fpay-logos/FOZZIL PAY LOGO black theme.png";
const logoLightTheme = "/fpay-logos/FOZZIL_PAY_LOGO_white theme.png";

const FPAY_API_URL = process.env.REACT_APP_FPAY_API_URL || 'https://fpaybeshadesecure.fozzilindustries.workers.dev';

// Premium Payment Icons (Matching Checkout Style)
const VisaSVG = ({ dark }) => (
  <svg width="24" height="16" viewBox="0 0 750 471" fill="none">
    <path d="M278.198 334.228L311.058 138.997H364.781L331.887 334.228H278.198Z" fill={dark ? "#FFFFFF" : "#1A1F71"} />
    <path d="M524.307 142.687C513.189 138.465 495.574 133.968 473.861 133.968C420.789 133.968 384.066 161.143 383.780 199.992C383.506 228.996 410.004 245.185 429.580 254.979C449.670 265.047 456.446 271.583 456.359 280.659C456.197 294.472 439.562 300.804 424.068 300.804C402.541 300.804 390.999 297.619 373.095 289.831L366.172 286.552L358.544 332.098C371.325 337.709 394.262 342.592 418.069 342.854C474.608 342.854 510.541 316.058 510.934 274.628C511.113 251.700 496.665 234.070 467.028 219.486C448.948 210.048 437.894 203.840 438.005 194.099C438.005 185.428 448.182 176.161 470.076 176.161C488.472 175.868 501.947 179.933 512.474 184.155L517.610 186.612L525.104 142.413L524.307 142.687Z" fill={dark ? "#FFFFFF" : "#1A1F71"} />
    <path d="M611.064 138.997C623.832 138.997 633.356 142.456 639.574 155.663L713.529 334.228H658.094L647.813 305.632H580.881L573.393 334.228H518.609L572.313 152.040C577.639 139.697 588.253 138.997 611.064 138.997ZM596.147 208.095L578.046 265.535H624.248L596.147 208.095Z" fill={dark ? "#FFFFFF" : "#1A1F71"} />
    <path d="M206.002 138.997L153.529 269.413L147.886 240.900C138.317 208.666 108.361 173.699 75.094 156.220L123.282 334.092L180.217 334.021L263.030 138.997H206.002Z" fill={dark ? "#FFFFFF" : "#1A1F71"} />
    <path d="M117.298 138.997H33.893L33.198 142.990C101.285 159.974 146.722 201.058 166.066 250.929L146.187 155.898C142.802 143.020 132.936 139.403 117.298 138.997Z" fill="#F7A600" />
  </svg>
);

const MastercardSVG = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="9" cy="12" r="7" fill="#EB001B" />
    <circle cx="15" cy="12" r="7" fill="#F79E1B" fillOpacity="0.8" />
  </svg>
);

const RupaySVG = ({ dark }) => (
  <svg width="40" height="15" viewBox="0 0 120 45" fill="none">
    <text x="2" y="32" fontFamily="Arial, sans-serif" fontWeight="bold" fontSize="28" fill={dark ? "#FFFFFF" : "#1C65B0"}>Ru</text>
    <text x="42" y="32" fontFamily="Arial, sans-serif" fontWeight="bold" fontSize="28" fill="#F48520">Pay</text>
    <path d="M95 8c6 0 11 2 15 5l-2 3c-3-2-7-4-13-4-5 0-10 2-13 5l-2-3c4-4 9-6 15-6z" fill="#F48520" />
    <path d="M95 37c-6 0-11-2-15-5l2-3c3 2 7 4 13 4 5 0 10-2 13-5l2 3c-4 4-9 6-15 6z" fill="#097A44" />
  </svg>
);

// Payment type icons with Metallic Styling
const PaymentTypeIcon = ({ type, dark, cardNumber = '' }) => {
  const isMetallic = type === 'Card';
  
  const getLogo = () => {
    switch (type) {
      case 'UPI':
        return <img src="/fpay-logos/upi-logo.png" alt="UPI" className="h-4 w-auto object-contain scale-[1.3]" />;
      case 'Card':
        // Default to Visa style branding for new card
        return <VisaSVG dark={!dark} />;
      case 'Net Banking':
        return <FaUniversity className={`w-5 h-5 ${dark ? 'text-white' : 'text-gray-900'}`} />;
      case 'Wallet':
        return <Wallet className={`w-5 h-5 ${dark ? 'text-white' : 'text-gray-900'}`} />;
      default:
        return <CreditCard className={`w-5 h-5 ${dark ? 'text-white' : 'text-gray-900'}`} />;
    }
  };

  return (
    <div className={`relative w-[52px] h-[34px] rounded-md overflow-hidden flex items-center justify-center border transition-all ${
      isMetallic 
        ? dark 
          ? 'bg-[linear-gradient(135deg,#bdc3c7_0%,#ecf0f1_45%,#ffffff_50%,#ecf0f1_55%,#bdc3c7_100%)] border-black/10' 
          : 'bg-[linear-gradient(135deg,#0f0f0f_0%,#2c2c2c_45%,#444444_50%,#2c2c2c_55%,#0f0f0f_100%)] border-white/10'
        : type === 'Net Banking' || type === 'Wallet'
          ? dark ? 'bg-black border-white/10' : 'bg-white border-black/5 shadow-sm'
          : dark ? 'bg-zinc-900 border-white/5' : 'bg-[#f2f2f7] border-transparent'
    }`}>
      {/* Glossy Overlay for Metallic */}
      {isMetallic && (
        <div className="absolute inset-0 bg-[linear-gradient(105deg,transparent_40%,rgba(255,255,255,0.1)_45%,rgba(255,255,255,0.2)_50%,rgba(255,255,255,0.1)_55%,transparent_60%)] pointer-events-none" />
      )}
      <div className="relative z-10 flex items-center justify-center w-full h-full p-1">
        {getLogo()}
      </div>
    </div>
  );
};

const FozzilPayModal = ({ isOpen, onClose, onSuccess, user, mode = 'save_method', onSwitchToCheckout }) => {
  const [theme, setTheme] = useState(
    window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  );
  const isDark = theme === "dark";
  
  const [paymentType, setPaymentType] = useState('UPI');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  
  // UPI Verification states
  const [isVerifyingVPA, setIsVerifyingVPA] = useState(false);
  const [vpaVerified, setVpaVerified] = useState(false);
  const [vpaPayeeName, setVpaPayeeName] = useState('');
  
  // Swipe selection for payment types
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const minSwipeDistance = 50;
  
  const paymentTypes = [
    { value: 'UPI', label: 'UPI' },
    { value: 'Card', label: 'Card' },
    { value: 'Net Banking', label: 'Net Banking' },
    { value: 'Wallet', label: 'Wallet' }
  ];

  const handleCycleType = (direction) => {
    const currentIndex = paymentTypes.findIndex(t => t.value === paymentType);
    let nextIndex;
    if (direction === 'next') {
      nextIndex = (currentIndex + 1) % paymentTypes.length;
    } else {
      nextIndex = (currentIndex - 1 + paymentTypes.length) % paymentTypes.length;
    }
    setPaymentType(paymentTypes[nextIndex].value);
  };

  // User profile from FOZZIL ID
  const [profile, setProfile] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    upi_id: '',
    card_number: '',
    card_expiry: '',
    card_cvv: '',
    card_name: '',
    bank_name: '',
    wallet_provider: ''
  });

  const dropdownRef = useRef(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setTheme(e.matches ? "dark" : "light");
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsTypeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch user profile and auto-fill when modal opens
  useEffect(() => {
    const fetchProfile = async () => {
      if (!isOpen || !user?.id) return;
      
      try {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();
        
        if (profileData) {
          setProfile(profileData);
          // Auto-fill card name with user's full name
          setFormData(prev => ({
            ...prev,
            card_name: `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim(),
            // If user has a saved UPI ID, pre-fill it
            upi_id: profileData.upi_id || ''
          }));
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
      }
    };

    if (isOpen) {
      setError('');
      setSuccess(false);
      setVpaVerified(false);
      setVpaPayeeName('');
      setPaymentType('UPI');
      fetchProfile();
    }
  }, [isOpen, user?.id]);

  // Reset VPA verification when UPI ID changes
  useEffect(() => {
    setVpaVerified(false);
    setVpaPayeeName('');
  }, [formData.upi_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Format card number with spaces
    if (name === 'card_number') {
      const cleaned = value.replace(/\D/g, '').slice(0, 16);
      const formatted = cleaned.replace(/(\d{4})/g, '$1 ').trim();
      setFormData(prev => ({ ...prev, [name]: formatted }));
      return;
    }
    
    // Format expiry (MM/YY)
    if (name === 'card_expiry') {
      const cleaned = value.replace(/\D/g, '').slice(0, 4);
      const formatted = cleaned.length > 2 ? `${cleaned.slice(0, 2)}/${cleaned.slice(2)}` : cleaned;
      setFormData(prev => ({ ...prev, [name]: formatted }));
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Verify UPI VPA
  const handleVerifyVPA = async () => {
    if (!formData.upi_id || !formData.upi_id.includes('@')) {
      setError('Please enter a valid UPI ID (e.g., name@upi)');
      return;
    }
    
    setIsVerifyingVPA(true);
    setError('');
    
    try {
      const response = await fetch(`${FPAY_API_URL}/api/payments/validate-vpa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vpa: formData.upi_id })
      });
      
      const result = await response.json();
      
      if (result.is_valid || result.status === 'VALID') {
        setVpaVerified(true);
        setVpaPayeeName(result.payerName || result.name || formData.upi_id.split('@')[0]);
      } else {
        setError('Invalid UPI ID. Please check and try again.');
        setVpaVerified(false);
      }
    } catch (err) {
      console.error('VPA verification error:', err);
      // Allow saving even if verification fails (API might be down)
      setVpaVerified(true);
      setVpaPayeeName(formData.upi_id.split('@')[0]);
    } finally {
      setIsVerifyingVPA(false);
    }
  };

  const getDisplayName = () => {
    switch (paymentType) {
      case 'UPI':
        return `UPI - ${formData.upi_id}`;
      case 'Card':
        const lastFour = formData.card_number.replace(/\s/g, '').slice(-4);
        return `Card ending ${lastFour}`;
      case 'Net Banking':
        return `${formData.bank_name} Net Banking`;
      case 'Wallet':
        return `${formData.wallet_provider} Wallet`;
      default:
        return 'Payment Method';
    }
  };

  const validateForm = () => {
    // If Card, we skip validation here as we delegate to the Checkout module
    if (paymentType === 'Card') return true;

    setError('');
    
    switch (paymentType) {
      case 'UPI':
        if (!formData.upi_id || !formData.upi_id.includes('@')) {
          setError('Please enter a valid UPI ID (e.g., user@upi)');
          return false;
        }
        if (!vpaVerified) {
          setError('Please verify your UPI ID first');
          return false;
        }
        break;
      case 'Card':
        const cardNum = formData.card_number.replace(/\s/g, '');
        if (cardNum.length !== 16) {
          setError('Please enter a valid 16-digit card number');
          return false;
        }
        if (!formData.card_expiry || formData.card_expiry.length !== 5) {
          setError('Please enter a valid expiry date (MM/YY)');
          return false;
        }
        if (!formData.card_cvv || formData.card_cvv.length < 3) {
          setError('Please enter a valid CVV');
          return false;
        }
        break;
      case 'Net Banking':
        if (!formData.bank_name) {
          setError('Please select a bank');
          return false;
        }
        break;
      case 'Wallet':
        if (!formData.wallet_provider) {
          setError('Please select a wallet provider');
          return false;
        }
        break;
      default:
        break;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    // Handle Card Redirection
    if (paymentType === 'Card') {
      if (onSwitchToCheckout) {
        onSwitchToCheckout();
        return;
      }
    }

    if (!validateForm()) return;
    
    setIsLoading(true);
    setError('');

    try {
      // Save payment method
      const paymentMethodData = {
        user_id: user?.id,
        payment_type: paymentType,
        display_name: getDisplayName(),
        provider: paymentType === 'UPI' ? formData.upi_id.split('@')[1] : 
                  paymentType === 'Net Banking' ? formData.bank_name :
                  paymentType === 'Wallet' ? formData.wallet_provider : 'card',
        last_four: paymentType === 'Card' ? formData.card_number.replace(/\s/g, '').slice(-4) : null,
        is_primary: true, // Make new method primary by default
        metadata: {
          ...(paymentType === 'UPI' && { 
            vpa: formData.upi_id,
            payee_name: vpaPayeeName 
          }),
          ...(paymentType === 'Card' && { 
            expiry: formData.card_expiry,
            name: formData.card_name 
          }),
          ...(paymentType === 'Net Banking' && { bank: formData.bank_name }),
          ...(paymentType === 'Wallet' && { provider: formData.wallet_provider })
        }
      };

      const result = await FozzilPay.savePaymentMethod(paymentMethodData);
      
      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess?.(result.data);
          onClose();
        }, 1500);
      } else {
        setError(result.message || 'Failed to save payment method');
      }
    } catch (err) {
      console.error('Payment method error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };


  const banks = [
    "Airtel Payments Bank", "AU Small Finance Bank", "Axis Bank", "Bandhan Bank",
    "Bank of India", "Bank of Maharashtra", "Canara Bank", "Catholic Syrian Bank",
    "Central Bank Of India", "City Union Bank", "Cosmos Bank", "DCB Bank",
    "Deutsche Bank", "Dhanlaxmi Bank", "Federal Bank", "HDFC Bank",
    "ICICI Bank", "IDBI Bank", "IDFC FIRST Bank", "Indian Bank",
    "Indian Overseas Bank", "IndusInd Bank", "Jammu & Kashmir Bank", 
    "Jana Small Finance Bank", "Janata Sahakari Bank Pune", "Karnataka Bank", 
    "Karur Vysya Bank", "Kotak Mahindra Bank", "Punjab National Bank", 
    "RBL Bank", "Saraswat Bank", "South Indian Bank", "Standard Chartered Bank", 
    "State Bank of India", "SVC Co-operative Bank Ltd.", "Tamilnad Mercantile Bank", 
    "UCO Bank", "Yes Bank"
  ].sort();

  const wallets = [
    "Amazon Pay", "Paytm", "PhonePe", "Freecharge", "Mobikwik", "JioMoney", "Ola Money", "Airtel Money"
  ].sort();

  if (!isOpen) return null;

  // PAYMENTS COMING SOON — intercept before main render
  if (!FozzilPay.paymentsEnabled) {
    return <PaymentsComingSoon isOpen={isOpen} onClose={onClose} />;
  }

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-6">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-md"
          onClick={onClose}
        />
        
        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 60 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 60 }}
          transition={{ 
            duration: 0.6,
            ease: [0.16, 1, 0.3, 1] // Premium Apple-like curve
          }}
          className={`relative w-full max-w-[820px] rounded-[32px] overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.5)] flex flex-col border backdrop-blur-3xl ${
            isDark ? 'bg-black text-white border-blue-500/25' : 'bg-white text-black border-blue-200'
          }`}
          style={{ minHeight: 'fit-content', maxHeight: '90vh' }}
        >
          {/* Blue Glows */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/15 blur-[100px] pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/10 blur-[80px] pointer-events-none" />
          {/* Header */}
          <div className={`flex items-center justify-between px-8 py-5 border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-1 overflow-hidden border ${isDark ? 'bg-black border-white/10' : 'bg-gray-100 border-gray-200'}`}>
                <img 
                  src="/logoicon.png" 
                  alt="FOZZIL" 
                  className="w-full h-full object-cover"
                />
              </div>
              <span className={`text-xl font-bold tracking-tighter ${isDark ? 'text-white' : 'text-black'}`}>Payment & Shipping</span>
            </div>
            <button
              onClick={onClose}
              className={`px-5 py-1.5 rounded-[32px] text-sm font-semibold transition-all border ${
                isDark 
                  ? 'bg-white/5 text-white hover:bg-white/10 border-white/10' 
                  : 'bg-gray-100 text-black hover:bg-gray-200 border-gray-200'
              }`}
            >
              Close
            </button>
          </div>



          {/* Content: Row Based Layout (Matching Screenshot) */}
          <div className="flex-grow flex flex-col pt-2 overflow-y-auto">
            
            {/* Row 1: Payment Method */}
            <div className={`flex flex-col md:flex-row items-start px-6 md:px-8 py-5 border-b ${isDark ? 'border-white/5' : 'border-black/5'}`}>
              <div className={`w-full md:w-[140px] shrink-0 mb-3 md:mb-0 md:pt-1 text-[10px] font-extrabold uppercase tracking-widest ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>
                Payment Method
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div 
                    className="flex items-center gap-4 cursor-pointer group"
                    onClick={() => handleCycleType('next')}
                    onTouchStart={e => touchStartX.current = e.targetTouches[0].clientX}
                    onTouchMove={e => touchEndX.current = e.targetTouches[0].clientX}
                    onTouchEnd={() => {
                      if (!touchStartX.current || !touchEndX.current) return;
                      const distance = touchStartX.current - touchEndX.current;
                      if (Math.abs(distance) > minSwipeDistance) handleCycleType(distance > 0 ? 'next' : 'prev');
                      touchStartX.current = null; touchEndX.current = null;
                    }}
                  >
                    <PaymentTypeIcon type={paymentType} dark={isDark} cardNumber={formData.card_number} />
                    <div>
                      <p className="font-bold text-sm tracking-tight flex items-center gap-1.5">
                        {paymentType} 
                        {(paymentType === 'UPI' && formData.upi_id) && <span className="text-zinc-400">- {formData.upi_id}</span>}
                        {(paymentType === 'Card' && formData.card_number) && <span className="text-zinc-400">- •••• {formData.card_number.slice(-4)}</span>}
                      </p>
                      <p className={`text-[11px] font-medium leading-tight ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                        {paymentType === 'UPI' ? (formData.upi_id || 'id@fozzilpay') : 
                         paymentType === 'Card' ? (formData.card_number || '•••• 4242') :
                         paymentType === 'Net Banking' ? (formData.bank_name || 'Choose Bank') : (formData.wallet_provider || 'Choose Wallet')}
                      </p>
                      <div className={`text-[9px] mt-1 flex items-center gap-1.5 font-bold ${isDark ? 'text-zinc-700' : 'text-zinc-300'}`}>
                        <span>&larr;</span>
                        <span className="uppercase tracking-widest">swipe</span>
                        <span>&rarr;</span>
                      </div>
                    </div>
                  </div>
                  <button 
                    onClick={() => handleCycleType('next')}
                    className="text-[10px] font-black text-[#007aff] uppercase tracking-widest hover:opacity-70 transition-opacity flex items-center gap-1"
                  >
                    CHANGE <ChevronDown className="w-3 h-3 rotate-[-90deg]" />
                  </button>
                </div>
              </div>
            </div>

            {/* Row 2: Contact */}
            <div className={`flex flex-col md:flex-row md:items-center px-6 md:px-8 py-5 border-b ${isDark ? 'border-white/5' : 'border-black/5'}`}>
              <div className={`w-full md:w-[140px] shrink-0 mb-2 md:mb-0 text-[10px] font-extrabold uppercase tracking-widest ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>
                Contact
              </div>
              <div className="flex-1 flex items-center justify-between">
                <span className={`font-bold text-sm truncate pr-4 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                  {profile?.email || 'rujaiananth25@gmail.com'}
                </span>
                <ChevronDown className={`w-4 h-4 rotate-[-90deg] shrink-0 ${isDark ? 'text-zinc-700' : 'text-zinc-300'}`} />
              </div>
            </div>

            {/* Row 3: Actionable Details (Inputs) */}
            <div className={`flex flex-col md:flex-row items-start md:items-center px-6 md:px-8 py-6`}>
              <div className={`w-full md:w-[140px] shrink-0 mb-4 md:mb-0 text-[10px] font-extrabold uppercase tracking-widest ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>
                Details
              </div>
              <div className="w-full md:flex-1">
                {paymentType === 'UPI' && (
                  <div className="w-full max-w-[350px]">
                    <div className={`relative flex items-center overflow-hidden h-12 rounded-full border transition-all ${
                      isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'
                    }`}>
                      <input
                        type="text"
                        name="upi_id"
                        value={formData.upi_id}
                        onChange={handleChange}
                        placeholder="yourname@upi"
                        disabled={vpaVerified}
                        className={`flex-1 bg-transparent outline-none px-4 text-sm font-bold ${isDark ? 'text-white placeholder-zinc-700' : 'text-black placeholder-zinc-300'}`}
                      />
                      <div className="pr-3">
                        {vpaVerified ? (
                          <div className="flex flex-col items-end">
                            <div className="flex items-center gap-1 text-green-500">
                              <CheckCircle2 className="w-4 h-4" />
                              <span className="text-[10px] font-black uppercase tracking-widest">Verified</span>
                            </div>
                            {vpaPayeeName && (
                              <span className="text-[8px] font-bold text-green-600 block leading-none mt-1 uppercase tracking-tighter">{vpaPayeeName}</span>
                            )}
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={handleVerifyVPA}
                            disabled={isVerifyingVPA || !formData.upi_id.includes('@')}
                            className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
                              isVerifyingVPA || !formData.upi_id.includes('@')
                                ? 'opacity-30 cursor-not-allowed'
                                : isDark ? 'bg-white text-black' : 'bg-black text-white'
                            }`}
                          >
                            {isVerifyingVPA ? '...' : 'Verify'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {paymentType === 'Card' && (
                  <div className={`w-full max-w-[350px] rounded-full px-5 py-2.5 flex items-center justify-between border ${isDark ? 'bg-blue-500/[0.08] border-blue-500/10' : 'bg-blue-50/50 border-blue-100'}`}>
                    <div className="flex items-center gap-2.5">
                      <Shield className={`w-4 h-4 ${isDark ? 'text-blue-400/50' : 'text-blue-600'}`} />
                      <p className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
                        Verification Required <span className="opacity-60 font-medium ml-1 text-[8px] lowercase italic">(₹2.00 refundable)</span>
                      </p>
                    </div>
                  </div>
                )}

                {paymentType === 'Net Banking' && (
                  <div className={`relative w-full max-w-[350px] h-12 flex items-center rounded-full overflow-hidden border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'}`}>
                    <select
                      name="bank_name"
                      value={formData.bank_name}
                      onChange={handleChange}
                      className={`w-full bg-transparent outline-none px-6 py-3.5 text-sm font-bold appearance-none ${isDark ? 'text-white' : 'text-black'}`}
                    >
                      <option value="">CHOOSE BANK</option>
                      {banks.map(bank => (
                        <option key={bank} value={bank} className="text-black">{bank}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-4 w-4 h-4 opacity-30 pointer-events-none" />
                  </div>
                )}

                {paymentType === 'Wallet' && (
                  <div className={`relative w-full max-w-[350px] h-12 flex items-center rounded-full overflow-hidden border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'}`}>
                    <select
                      name="wallet_provider"
                      value={formData.wallet_provider}
                      onChange={handleChange}
                      className={`w-full bg-transparent outline-none px-6 py-3.5 text-sm font-bold appearance-none ${isDark ? 'text-white' : 'text-black'}`}
                    >
                      <option value="">CHOOSE WALLET</option>
                      {wallets.map(wallet => (
                        <option key={wallet} value={wallet} className="text-black">{wallet}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-4 w-4 h-4 opacity-30 pointer-events-none" />
                  </div>
                )}
              </div>
            </div>

            {/* Error Message Display Area */}
            {error && (
              <div className="px-6 md:px-8 pb-4">
                <div className={`flex items-center gap-2 text-red-500 text-[10px] font-bold uppercase tracking-widest md:pl-[140px]`}>
                  <AlertCircle className="w-3.5 h-3.5" />
                  <span>{error}</span>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-8 pb-8 flex flex-col items-center gap-6 mt-2">
            <button
              onClick={handleSubmit}
              disabled={isLoading || success || (paymentType === 'UPI' && !vpaVerified)}
              className={`w-[140px] py-2.5 rounded-full font-black uppercase tracking-widest transition-all text-xs shadow-xl ${
                isLoading || success || (paymentType === 'UPI' && !vpaVerified)
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : isDark 
                    ? 'bg-white text-black hover:scale-105 active:scale-95' 
                    : 'bg-black text-white hover:scale-105 active:scale-95'
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Saving...
                </span>
              ) : success ? (
                'Saved!'
              ) : (
                paymentType === 'Card' ? 'Verify' : 'Done'
              )}
            </button>

            {/* Security Badge (Centered below buttons) */}
            <div className={`flex flex-wrap items-center justify-center gap-4 md:gap-6 text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              <div className="flex items-center gap-1.5 grayscale opacity-70">
                <Lock className="w-3 h-3" />
                <span className="uppercase tracking-widest font-bold">Secured by FOZZIL Pay</span>
              </div>
              <div className="flex items-center gap-1.5 grayscale opacity-70">
                <Shield className="w-3 h-3" />
                <span className="uppercase tracking-widest font-bold">RBI Compliant</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default FozzilPayModal;
